import axios from "axios";
import CryptoJS from "crypto-js";

const ONBOARDING_EVENT = "investorOnboardingUpdated";

function loginStoreKey() {
  return process.env.REACT_APP_LOGIN_DATA_STORE;
}

function loginSecret() {
  return process.env.REACT_APP_LOGIN_SEC;
}

function apiBase() {
  return String(process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/+$/, "");
}

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Read the logged-in user from encrypted session storage (same as LocState).
 */
export function readStoredUser() {
  try {
    const cryptUserData = localStorage.getItem(loginStoreKey());
    if (!cryptUserData) return null;
    const bytes = CryptoJS.AES.decrypt(cryptUserData, loginSecret());
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    if (!decrypted) return null;
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

/**
 * Merge fields into stored session user (e.g. mutual_status after onboarding).
 */
export function patchStoredUserData(updates) {
  const current = readStoredUser();
  if (!current) return null;
  const next = { ...current, ...updates };
  const encryptedData = CryptoJS.AES.encrypt(
    JSON.stringify(next),
    loginSecret()
  ).toString();
  localStorage.setItem(loginStoreKey(), encryptedData);
  return next;
}

/**
 * mutual_status: 0 = not completed, 1 = completed (user table).
 */
export function isInvestorOnboardingComplete(userOrId) {
  if (userOrId && typeof userOrId === "object") {
    return Number(userOrId.mutual_status) === 1;
  }
  const user = readStoredUser();
  return Number(user?.mutual_status) === 1;
}

/**
 * Fetch mutual_status from server and sync encrypted session.
 */
export async function refreshMutualStatusFromServer() {
  const res = await axios.get(`${apiBase()}/user/me/mutual-status`, {
    headers: authHeaders(),
    withCredentials: true,
  });

  const status = res.data?.status;
  const payload = res.data?.data;
  if (status !== 1 && status !== "1" && !payload) {
    throw new Error(res.data?.message || "Could not load mutual status");
  }

  const mutual_status = Number(payload?.mutual_status) || 0;
  const next = patchStoredUserData({ mutual_status });
  window.dispatchEvent(new CustomEvent(ONBOARDING_EVENT));
  return next;
}

/**
 * After successful BSE investor registration: set mutual_status = 1 in DB, verify via API, sync session.
 */
export async function markInvestorOnboardingSubmitted() {
  await axios.post(
    `${apiBase()}/user/mutual-onboarding/complete`,
    {},
    {
      headers: authHeaders(),
      withCredentials: true,
    }
  );

  return refreshMutualStatusFromServer();
}
